import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import {
  BUILDER_LINKS,
  TEMPLATE_LINKS,
  FRESHER_LINKS,
  ROLE_LINKS,
  JOBSEARCH_LINKS,
  GUIDE_LINKS,
  COMPANY_LINKS,
  getUncategorizedLandingLinks,
  type NavLink,
} from "@/data/navigation";
import { cn } from "@/lib/utils";

/** Small-caps column label. Sits a step below body copy in emphasis without
    dropping to `text-subtle`, which fails AA at this size. */
const FOOT_HEADING =
  "font-sans text-xs font-semibold uppercase tracking-wide text-muted-foreground";

const FOOT_LINK =
  "inline-block py-0.5 text-sm leading-snug text-muted-foreground transition-colors duration-180 ease-premium hover:text-foreground focus-visible:text-foreground";

function FootCol({ heading, links }: { heading: string; links: NavLink[] }) {
  if (!links.length) return null;
  return (
    <div>
      <h3 className={cn(FOOT_HEADING, "mb-4")}>{heading}</h3>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={`${heading}-${l.to}-${l.label}`}>
            <Link
              to={l.to}
              title={l.title}
              lang={l.hindi ? "hi" : undefined}
              className={cn(FOOT_LINK, l.hindi && "font-medium text-foreground")}
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SiteFooter() {
  const other = getUncategorizedLandingLinks();
  return (
    <footer className="border-t border-border bg-surface-sunken mt-20">
      <div className="container mx-auto px-4 py-14 md:py-16">
        <div className="pb-10 border-b border-border">
          <Link
            to="/"
            className="inline-flex items-center gap-2.5 mb-4 rounded-md transition-opacity duration-180 ease-premium hover:opacity-85"
          >
            <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center shadow-xs">
              <FileText className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-display text-lg font-bold tracking-tight text-foreground">AI Resume Studio</span>
          </Link>
          <p className="text-sm leading-relaxed text-muted-foreground max-w-xl">
            Build a professional, ATS-friendly resume in minutes. Free templates, AI assistance, and
            downloadable Word and PDF files — no login required.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-5 sm:gap-x-6 lg:gap-x-8 gap-y-10 pt-10">
          <FootCol heading="Resume Builders" links={BUILDER_LINKS} />
          <FootCol heading="Templates & Examples" links={TEMPLATE_LINKS} />
          <FootCol heading="For Freshers" links={FRESHER_LINKS} />
          <FootCol heading="Resume by Role" links={ROLE_LINKS} />
          <FootCol heading="Guides & Job Search" links={[...GUIDE_LINKS, ...JOBSEARCH_LINKS]} />
          <FootCol heading="Company" links={COMPANY_LINKS} />
        </div>

        {other.length > 0 && (
          <div className="pt-10 border-t border-border mt-12">
            <h3 className={cn(FOOT_HEADING, "mb-4")}>More pages</h3>
            <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 sm:gap-x-6 lg:gap-x-8 gap-y-2">
              {other.map((p) => (
                <li key={p.to}>
                  <Link to={p.to} title={p.title} className={FOOT_LINK}>
                    {p.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="border-t border-border mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center md:text-left">
            &copy; {new Date().getFullYear()} AI Resume Studio. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <Link to="/" className={FOOT_LINK}>Home</Link>
            <Link to="/about" className={FOOT_LINK}>About</Link>
            <Link to="/contact" className={FOOT_LINK}>Contact</Link>
            <Link to="/sitemap-html" className={FOOT_LINK}>Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
